import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { PasswordPolicyDto } from '../../../../api/model/passwordPolicyDto';
import { PasswordPolicyService } from '../../../../auth/services/password-policy.service';
import { FormElementComponent } from '../../../../../shared/components/form-inputs/components/form-element/form-element.component';
import { LoaderComponent } from '../../../../../shared/components/loader/loader.component';
import { LegalLinksContainerComponent } from '../../../../../shared/components/legal-links-container/legal-links-container.component';
import { PasswordRequirementsPipe } from '../../../../../shared/pipes/password-requirements.pipe';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { TogglePasswordDirective } from '../../../../../shared/directives/toggle-password.directive';
import { PasswordStrengthComponent } from '../../../../../shared/components/password-strength/password-strength.component';
import {
  passwordMatchValidator,
  passwordStrengthValidator,
  passwordStrengthValidatorWithPolicy,
  isPasswordValid
} from '../../../../../shared/validators/password.validators';
import { FeedbackService } from '../../../../../services/feedback.service';
import { AuthService } from '../../../../../services/auth.service';
import { UserService } from '../../../../../services/user.service';

@Component({
  selector: 'app-reset-password',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    FormElementComponent,
    LoaderComponent,
    LegalLinksContainerComponent,
    PasswordRequirementsPipe,
    TranslocoDirective,
    TogglePasswordDirective,
    PasswordStrengthComponent
  ],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.css',
  standalone: true
})
export class ResetPasswordComponent implements OnInit {
  private readonly _authService = inject(AuthService);
  private readonly _userService = inject(UserService);
  private readonly _route = inject(ActivatedRoute);
  private readonly _feedbackService = inject(FeedbackService);
  private readonly _translocoService = inject(TranslocoService);
  private readonly _router = inject(Router);
  private readonly _passwordPolicyService = inject(PasswordPolicyService);

  public resetToken = '';
  public email = '';
  public isLoading = false;
  public passwordPolicy: PasswordPolicyDto | null = null;

  public passwordResetForm: FormGroup;

  constructor() {
    this.passwordResetForm = new FormGroup(
      {
        password: new FormControl('', [Validators.required, passwordStrengthValidator()]),
        confirmPassword: new FormControl('', [Validators.required])
      },
      { validators: passwordMatchValidator() }
    );
  }

  async ngOnInit(): Promise<void> {
    const tokenAndEmail = this._route.snapshot.paramMap.get('tokenAndEmail') || '';

    if (tokenAndEmail.includes('&email=')) {
      const parts = tokenAndEmail.split('&email=');
      this.resetToken = parts[0];
      this.email = decodeURIComponent(parts[1]);
    } else {
      this.resetToken = tokenAndEmail;
    }

    if (!this.resetToken) {
      this._router.navigate(['/auth/forgot-password']);
      return;
    }

    const policy = await this._passwordPolicyService.ensurePolicyLoaded();
    this.passwordPolicy = policy;
    const passwordControl = this.passwordResetForm.get('password')!;
    passwordControl.setValidators([Validators.required, passwordStrengthValidatorWithPolicy(policy)]);
    passwordControl.updateValueAndValidity();
  }

  public get password(): string {
    return this.passwordResetForm.get('password')?.value || '';
  }

  public get confirmPassword(): string {
    return this.passwordResetForm.get('confirmPassword')?.value || '';
  }

  public get isPasswordValid(): boolean {
    return isPasswordValid(this.password, this.confirmPassword, this.passwordPolicy);
  }

  public onConfirmPasswordFocus(): void {
    const confirmPasswordControl = this.passwordResetForm.get('confirmPassword');
    if (confirmPasswordControl && !confirmPasswordControl.touched) {
      confirmPasswordControl.markAsTouched();
      // Trigger validation by updating the form
      this.passwordResetForm.updateValueAndValidity();
    }
  }

  public async onSubmit(): Promise<void> {
    if (this.passwordResetForm.invalid || !this.resetToken) {
      return;
    }

    const formValues = this.passwordResetForm.value;
    if (formValues.password !== formValues.confirmPassword) {
      console.error('Passwords do not match');
      return;
    }

    this.isLoading = true;

    try {
      await this._authService.resetPasswordAndLogin(this.resetToken, formValues.password);

      // Load user data after successful password reset and login
      await this._userService.loadCurrentUser();

      this._feedbackService.success(this._translocoService.translate('successMessages.passwordChangedSuccessfully'));

      // Redirect to dashboard after successful password reset and login
      this._router.navigate(['/main/dashboard']);
    } catch (error) {
      console.error('Failed to reset password:', error);
      this._feedbackService.error(this._translocoService.translate('errors.generalError'));
    } finally {
      this.isLoading = false;
    }
  }
}
